import React, { useState } from 'react';
import { isMotionInOrder } from '../../utils/motionTypes';
import ContextHelpTip from '../common/ContextHelpTip';

const MOTION_PALETTE_HINT = 'Only in-order motions are selectable.';

const PANEL_CLASS = 'rounded-lg border border-pp-line/80 bg-pp-paper/60 p-2.5';

const MotionMenu = ({
  theme,
  motionsByClass,
  onMotionSelect,
  unanimousConsentRequests = [],
  onUnanimousConsent,
  currentMotion,
  motionStack = [],
}) => {
  const [adHocUcText, setAdHocUcText] = useState('');
  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';

  const checkMotionInOrder = (motion) => {
    return isMotionInOrder(motion.key, currentMotion, motionStack);
  };

  const submitAdHocUc = () => {
    const text = String(adHocUcText || '').trim();
    if (!text || !onUnanimousConsent) return;
    onUnanimousConsent(text);
    setAdHocUcText('');
  };

  return (
    <div
      className={`flex h-full min-h-0 flex-col ${cardClass} rounded-xl shadow-pp`}
    >
      <div className="flex shrink-0 items-center gap-1.5 border-b border-pp-line/60 px-3 py-2.5">
        <h2
          className={`text-sm font-semibold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}
        >
          Motions
        </h2>
        <ContextHelpTip text={MOTION_PALETTE_HINT} theme={theme === 'dark' ? 'dark' : 'light'} />
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {/* UC presets (org) + ad hoc entry */}
        <div className={PANEL_CLASS}>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-pp-muted">
            Unanimous consent
          </h3>
          <select
            onChange={(e) => {
              if (e.target.value && onUnanimousConsent) {
                onUnanimousConsent(e.target.value);
                e.target.value = '';
              }
            }}
            className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm text-pp-ink"
            defaultValue=""
          >
            <option value="">Select a UC request...</option>
            {unanimousConsentRequests.map((request, index) => (
              <option key={index} value={request}>
                {request}
              </option>
            ))}
          </select>
          <div className="mt-2 space-y-1.5">
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-pp-muted">
              Ad hoc request
            </label>
            <input
              type="text"
              value={adHocUcText}
              onChange={(e) => setAdHocUcText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitAdHocUc();
                }
              }}
              placeholder="e.g. Approve the treasurer’s report"
              className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm text-pp-ink"
            />
            <button
              type="button"
              disabled={!String(adHocUcText || '').trim()}
              onClick={submitAdHocUc}
              className="w-full rounded-lg bg-pp-sage px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Request unanimous consent
            </button>
          </div>
        </div>

        {Object.entries(motionsByClass).map(([className, classMotions]) => (
          <div key={className} className={PANEL_CLASS}>
            <h3
              className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${
                theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'
              }`}
            >
              {className}
            </h3>
            <div className="space-y-0.5">
              {classMotions.map((motion) => {
                const inOrder = checkMotionInOrder(motion);
                return (
                  <button
                    key={motion.key}
                    type="button"
                    disabled={!inOrder}
                    onClick={() => inOrder && onMotionSelect(motion.key)}
                    title={motion.description}
                    className={`w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                      inOrder
                        ? `hover:bg-pp-surface/90 ${theme === 'dark' ? 'hover:bg-gray-700' : ''}`
                        : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{motion.name}</span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          inOrder ? 'bg-pp-sageSoft text-pp-sage' : 'bg-red-50 text-red-800'
                        }`}
                      >
                        {inOrder ? 'In order' : 'Not in order'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MotionMenu;
